import { useState, useMemo } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { Icons } from "../../utils/react-svg";
import Text from "../Text";
import ElementsWrapper from "../checkout/ElementsWrapper";
import StripeExpressCheckout from "../checkout/StripeExpressCheckout";
import Cart from "../../store/cart/model";
import CartProduct from "../../store/cart-product/model";
import Coupon from "../../store/coupon/model";
import useVariation from "../../hooks/useVariation";
import { navigate } from "../../services/navigation";
import Prop65Warning from "../checkout/Prop65Warning";
import productSelectors from "../../store/product/selectors";
import { ProductSKU } from "../../utils/product";
import { shouldShowProp65 } from "../../utils/detectRegion";
import Product from "../../store/product/model";

const BottomCheckoutButtonContainer = styled.div`
  width: 100%;
  border-top: 1px solid var(--cool-40);
  padding: var(--spacing-1) var(--spacing-1_5)
    calc(var(--spacing-1) + env(safe-area-inset-bottom));

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: var(--spacing-0_5);
`;

const BottomButton = styled.button`
  display: flex;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  border-radius: 25px;
  border: none;
  background: var(--indigo-blue);
  color: var(--white);
  height: 48px;
  transition:
    background 0.2s,
    color 0.2s;
  font-size: 16px;
  line-height: 24px;

  // Set min-width to share space with Apple Pay when present
  min-width: calc(50% - var(--spacing-0_25));
  width: 100%;

  svg {
    path,
    rect {
      transition: stroke 0.2s;
    }
  }

  &:hover {
    background: var(--yellow-ochre);
    color: var(--indigo-blue);

    svg {
      path,
      rect {
        stroke: var(--indigo-blue);
      }
    }
  }
`;

const ButtonCopy = styled.span.attrs({ className: "typography-label1" })`
  font-size: var(--spacing-1, 16px) !important;
`;

type Props = {
  activeCart: Cart;
  isAddingToSubscription: boolean;
  isProcessing: boolean;
  cartProducts: CartProduct[];
  cartQuantity: number;
  activeCoupon: Coupon;
  handleCheckoutCTAClicked: React.MouseEventHandler<HTMLButtonElement>;
  setExpressCheckoutProcessing: React.ComponentProps<
    typeof StripeExpressCheckout
  >["setExpressCheckoutProcessing"];
};

const FlyoutCartBottom = ({
  activeCart,
  isAddingToSubscription,
  isProcessing,
  cartProducts,
  cartQuantity,
  activeCoupon,
  handleCheckoutCTAClicked,
  setExpressCheckoutProcessing,
}: Props) => {
  const [showErrorBanner, setShowErrorBanner] = useState(false);

  const quizEntry = useVariation("quiz-entry-empty-cart-flyout", false);

  const canCheckout = !!(!isProcessing && cartQuantity);
  const showQuizEntry = quizEntry && !isProcessing && !canCheckout;
  const expressCheckoutEligible =
    canCheckout && !isAddingToSubscription && activeCart.total > 0;

  const navigateToQuiz = () => {
    navigate("/quiz");
  };

  // Efficiently select only the products data we need
  const allProducts = useSelector(productSelectors.allProducts) as Product[];
  const shouldShowProp65Warning = useMemo(() => {
    // Check if cart contains PDCH product
    if (!cartProducts || cartProducts.length === 0) {
      return false;
    }

    const hasPDCH = cartProducts.some((cartProduct) => {
      const product = allProducts.find(
        (p: Product) => p.id === cartProduct.productId,
      );
      return product?.sku === ProductSKU.PDCH;
    });

    // Only show if PDCH in cart AND visitor is in/from California
    // Use shouldShowProp65() without shipping address - includes failsafe for US visitors
    return hasPDCH && shouldShowProp65();
  }, [cartProducts, allProducts]);

  return (
    <ElementsWrapper>
      {shouldShowProp65Warning && canCheckout && !isProcessing && (
        <div style={{ padding: "0 var(--spacing-1_5)" }}>
          <Prop65Warning />
        </div>
      )}
      <BottomCheckoutButtonContainer>
        {showQuizEntry ? (
          <BottomButton
            className="fullwidth"
            aria-label="Take Our Quiz"
            onClick={navigateToQuiz}
          >
            <ButtonCopy>Take Our Quiz</ButtonCopy>
          </BottomButton>
        ) : (
          <>
            {showErrorBanner && (
              <p>
                We encountered an error with your payment. Please try again.
              </p>
            )}
            {expressCheckoutEligible && (
              <StripeExpressCheckout
                cart={activeCart}
                cartProducts={cartProducts}
                setShowErrorBanner={setShowErrorBanner}
                supportedExpressCheckoutPaymentMethods="applePay"
                coupon={activeCoupon}
                setExpressCheckoutProcessing={setExpressCheckoutProcessing}
                location="flyout_cart"
                showPayAnotherWay={false}
              />
            )}
            <BottomButton
              disabled={!canCheckout}
              className="fullwidth"
              onClick={handleCheckoutCTAClicked}
              aria-label="Checkout"
              aria-disabled={!canCheckout}
            >
              <Icons.CreditCard />
              <ButtonCopy>
                <Text
                  id={"cart.flyout.button-checkout"}
                  defaultMessage={"Checkout"}
                />
              </ButtonCopy>
            </BottomButton>
          </>
        )}
      </BottomCheckoutButtonContainer>
    </ElementsWrapper>
  );
};

export default FlyoutCartBottom;
